import React, { useState } from 'react';
import styled from 'styled-components';

const ContactSection = styled.section`
  padding: 5rem 0;
  background-color: var(--background-light);
`;

const SectionContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionTitle = styled.h2`
  font-family: 'Libre Baskerville', serif;
  font-size: 2.5rem;
  color: var(--primary-color);
  text-align: center;
  margin-bottom: 1rem;
`;

const SectionSubtitle = styled.h3`
  font-family: 'Montserrat', sans-serif;
  font-size: 1.2rem;
  color: var(--accent-color);
  text-align: center;
  margin-bottom: 3rem;
`;

const ContactContent = styled.div`
  display: flex;
  gap: 3rem;
  
  @media (max-width: 992px) {
    flex-direction: column;
  }
`;

const ContactInfo = styled.div`
  flex: 1;
`;

const InfoCard = styled.div`
  background-color: var(--background-white);
  padding: 2rem;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  margin-bottom: 2rem;
`;

const InfoTitle = styled.h4`
  font-family: 'Montserrat', sans-serif;
  font-size: 1.2rem;
  color: var(--primary-color);
  margin-bottom: 1rem;
`;

const InfoList = styled.ul`
  list-style: none;
`;

const InfoItem = styled.li`
  font-family: 'Lato', sans-serif;
  margin-bottom: 1rem;
  display: flex;
  align-items: flex-start;
  
  svg {
    margin-right: 0.8rem;
    color: var(--accent-color);
    min-width: 20px;
  }
`;

const InfoLink = styled.a`
  color: var(--primary-color);
  text-decoration: none;
  transition: color 0.3s ease;
  cursor: pointer;
  
  &:hover {
    color: var(--accent-color);
  }
`;

// Modal styles for email form popup
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background-color: var(--background-white);
  border-radius: 10px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background-color: var(--primary-color);
  color: var(--text-light);
`;

const ModalTitle = styled.h3`
  font-family: 'Montserrat', sans-serif;
  font-size: 1.3rem;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-light);
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.3s ease;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }
`;

const Form = styled.form`
  padding: 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const FormLabel = styled.label`
  display: block;
  font-family: 'Montserrat', sans-serif;
  font-size: 0.9rem;
  color: var(--primary-color);
  margin-bottom: 0.5rem;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.8rem;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-family: 'Lato', sans-serif;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: var(--accent-color);
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 0.8rem;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-family: 'Lato', sans-serif;
  font-size: 1rem;
  min-height: 150px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: var(--accent-color);
  }
`;

const SubmitButton = styled.button`
  display: inline-block;
  padding: 0.8rem 1.8rem;
  background-color: var(--accent-color);
  color: var(--text-light);
  border: none;
  border-radius: 5px;
  font-family: 'Montserrat', sans-serif;
  font-weight: 500;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.3s ease;
  
  &:hover {
    background-color: var(--primary-color);
  }
`;

const ThankYouMessage = styled.div`
  background-color: var(--accent-color);
  color: var(--text-light);
  padding: 1rem;
  border-radius: 5px;
  margin-bottom: 1rem;
  text-align: center;
  font-family: 'Montserrat', sans-serif;
`;


const BookingSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: var(--background-white);
  padding: 2rem;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  text-align: center;
`;

const BookingTitle = styled.h3`
  font-family: 'Montserrat', sans-serif;
  font-size: 1.5rem;
  color: var(--primary-color);
  margin-bottom: 1rem;
`;

const BookingDescription = styled.p`
  font-family: 'Lato', sans-serif;
  color: var(--text-dark);
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const BookingButton = styled.button`
  display: inline-block;
  padding: 1rem 2rem;
  background-color: var(--accent-color);
  color: var(--text-light);
  border: none;
  border-radius: 5px;
  font-family: 'Montserrat', sans-serif;
  font-weight: 500;
  font-size: 1.1rem;
  cursor: pointer;
  transition: background-color 0.3s ease;
  
  &:hover {
    background-color: var(--primary-color);
  }
`;

const Contact = () => {
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  /**
   * Opens the booking system in a new window.
   */
  const openBookingWindow = () => {
    window.open(
      "https://mychart.myoryx.ca/online-schedule/index.html?realm=finchdudley&univers=com",
      "_blank",
      "width=1000,height=700,scrollbars=yes,resizable=yes"
    );
  };

  /**
   * Opens the email form modal popup.
   */
  const openEmailModal = () => {
    setShowEmailModal(true);
  };

  /**
   * Closes the email form modal popup.
   */
  const closeEmailModal = () => {
    setShowEmailModal(false);
    setSubmitted(false);
    setFormData({
      name: '',
      email: '',
      phone: '',
      message: ''
    });
  };

  /**
   * Handles clicking outside the modal to close it.
   * @param {Event} e - The click event.
   */
  const handleModalOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      closeEmailModal();
    }
  };

  /**
   * Handles form input changes.
   * @param {Event} e - The input change event.
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  /**
   * Handles form submission by sending data to Google Forms.
   * @param {Event} e - The form submission event.
   */
  const handleSubmit = (e) => {
    e.preventDefault();

    // Google Form POST URL (replace 'viewform' with 'formResponse')
    const googleFormUrl = "https://docs.google.com/forms/d/e/1FAIpQLSc3OM9ar8ELZdvAEKlESU4X8wUa2xBPud5NQmSiJi57Y0TubQ/formResponse";

    // Prepare form data for Google Forms
    const formDataToGoogle = new FormData();
    formDataToGoogle.append("entry.314368273", formData.name);
    formDataToGoogle.append("entry.1254944539", formData.email);
    formDataToGoogle.append("entry.165465848", formData.phone);
    formDataToGoogle.append("entry.962685840", formData.message);

    // Send the form data to Google Forms
    fetch(googleFormUrl, {
      method: "POST",
      mode: "no-cors", // Google Forms requires no-cors
      body: formDataToGoogle,
    })
      .then(() => {
        setSubmitted(true);
        setFormData({
          name: "",
          email: "",
          phone: "",
          message: ""
        });
        setTimeout(() => {
          setSubmitted(false);
          closeEmailModal();
        }, 3000);
      })
      .catch((error) => {
        // Log error to console for debugging
        // Note: no-cors mode will not return errors for failed submissions
        console.error("Error submitting to Google Forms:", error);
      });
  };
  
  return (
    <ContactSection id="contact">
      <SectionContainer>
        <SectionTitle>Contact Us</SectionTitle>
        <SectionSubtitle>Get in Touch for Any Questions or to Schedule an Appointment</SectionSubtitle>
        
        <ContactContent>
          <ContactInfo>
            <InfoCard>
              <InfoTitle>Contact Information</InfoTitle>
              <InfoList>
                <InfoItem>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/>
                  </svg>
                  Lower Level, 78 Finch Avenue East, North York, Ontario, M2N 7J5
                </InfoItem>
                <InfoItem>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M3.654 1.328a.678.678 0 0 0-1.015-.063L1.605 2.3c-.483.484-.661 1.169-.45 1.77a17.568 17.568 0 0 0 4.168 6.608 17.569 17.569 0 0 0 6.608 4.168c.601.211 1.286.033 1.77-.45l1.034-1.034a.678.678 0 0 0-.063-1.015l-2.307-1.794a.678.678 0 0 0-.58-.122l-2.19.547a1.745 1.745 0 0 1-1.657-.459L5.482 8.062a1.745 1.745 0 0 1-.46-1.657l.548-2.19a.678.678 0 0 0-.122-.58L3.654 1.328zM1.884.511a1.745 1.745 0 0 1 2.612.163L6.29 2.98c.329.423.445.974.315 1.494l-.547 2.19a.678.678 0 0 0 .178.643l2.457 2.457a.678.678 0 0 0 .644.178l2.189-.547a1.745 1.745 0 0 1 1.494.315l2.306 1.794c.829.645.905 1.87.163 2.611l-1.034 1.034c-.74.74-1.846 1.065-2.877.702a18.634 18.634 0 0 1-7.01-4.42 18.634 18.634 0 0 1-4.42-7.009c-.362-1.03-.037-2.137.703-2.877L1.885.511z"/>
                  </svg>
                  <InfoLink href="tel:4162250808">416-225-0808</InfoLink>
                </InfoItem>
                <InfoItem>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4Zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1H2Zm13 2.383-4.708 2.825L15 11.105V5.383Zm-.034 6.876-5.64-3.471L8 9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741ZM1 11.105l4.708-2.897L1 5.383v5.722Z"/>
                  </svg>
                  <InfoLink href="#" onClick={(e) => { e.preventDefault(); openEmailModal(); }}>info@finchdudley.com</InfoLink>
                </InfoItem>
                <InfoItem>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                    <path d="M11 8a3 3 0 1 1-6 0 3 3 0 0 1 6 0z"/>
                  </svg>
                  <InfoLink href="https://finchdudley.com" target="_blank" rel="noopener noreferrer">finchdudley.com</InfoLink>
                </InfoItem>
              </InfoList>
            </InfoCard>
            
            <InfoCard>
              <InfoTitle>Hours of Operation</InfoTitle>
              <InfoList>
                <InfoItem>Friday: 10:00 AM - 5:00 PM</InfoItem>
            
              </InfoList>
            </InfoCard>
          </ContactInfo>
          
          <BookingSection>
            <BookingTitle>Book Your Appointment</BookingTitle>
            <BookingDescription>
              Schedule your appointment online using our convenient booking system. 
              Click the button below to access our online scheduling portal where you can 
              choose your preferred date and time.
            </BookingDescription>
            <BookingButton onClick={openBookingWindow}>
              Schedule Appointment
            </BookingButton>
          </BookingSection>
        </ContactContent>
      </SectionContainer>
      
      {/* Email Form Modal */}
      {showEmailModal && (
        <ModalOverlay onClick={handleModalOverlayClick}>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>Send us a Message</ModalTitle>
              <CloseButton onClick={closeEmailModal}>&times;</CloseButton>
            </ModalHeader>
            <Form onSubmit={handleSubmit}>
              {submitted && (
                <ThankYouMessage>
                  Thank you for your message! We'll be in touch soon.
                </ThankYouMessage>
              )}
              
              <FormGroup>
                <FormLabel htmlFor="name">Full Name</FormLabel>
                <FormInput 
                  type="text" 
                  id="name" 
                  name="name" 
                  value={formData.name} 
                  onChange={handleChange} 
                  required 
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel htmlFor="email">Email Address</FormLabel>
                <FormInput 
                  type="email" 
                  id="email" 
                  name="email" 
                  value={formData.email} 
                  onChange={handleChange} 
                  required 
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel htmlFor="phone">Phone Number</FormLabel>
                <FormInput 
                  type="tel" 
                  id="phone" 
                  name="phone" 
                  value={formData.phone} 
                  onChange={handleChange} 
                  required 
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel htmlFor="message">Your Message</FormLabel>
                <FormTextarea 
                  id="message" 
                  name="message" 
                  value={formData.message} 
                  onChange={handleChange} 
                  required 
                />
              </FormGroup>
              
              <SubmitButton type="submit">Send Message</SubmitButton>
            </Form>
          </ModalContent>
        </ModalOverlay>
      )}
    </ContactSection>
  );
};

export default Contact; 